// Uses API endpoints - NO getServiceRoleClient() in browser
import { ensureSessionHeaders } from './serverSession'

// API helper for legal document operations with timeout
async function callLegalDocumentAPI(operation: string, data: Record<string, any> = {}, timeoutMs: number = 30000): Promise<any> {
 const sessionHeaders = await ensureSessionHeaders();
 console.log('LEGAL API: Session headers present:', Object.keys(sessionHeaders).length > 0, 'for operation:', operation);

 // Create an AbortController for timeout
 const controller = new AbortController();
 const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

 try {
  const response = await fetch('/api/user/legal-documents', {
   method: 'POST',
   headers: { 'Content-Type': 'application/json', ...sessionHeaders },
   body: JSON.stringify({ operation, data }),
   signal: controller.signal
  });

  clearTimeout(timeoutId);

  if (!response.ok) {
   const errorBody = await response.text().catch(() => 'unknown');
   console.error('LEGAL API: Request failed:', response.status, errorBody);
   throw new Error(`Legal document API error: ${response.status}`);
  }

  return response.json();
 } catch (error: any) {
  clearTimeout(timeoutId);

  if (error.name === 'AbortError') {
   console.error('LEGAL API: Request timed out after', timeoutMs, 'ms');
   throw new Error('Request timed out. Please check your internet connection and try again.');
  }

  throw error;
 }
}

export interface LegalDocumentAgreement {
 id: number
 user_id: number
 document_type: 'terms_conditions' | 'privacy_policy' | 'disclaimer'
 document_version: string
 agreed_at: string
 ip_address?: string
 user_agent?: string
 is_current: boolean
 created_at: string
 updated_at: string
}

export interface AgreementStatus {
 terms_conditions: boolean
 privacy_policy: boolean
 disclaimer: boolean
 share_purchase_agreement: boolean
 all_agreed: boolean
}

export class LegalDocumentService {
 private static readonly CURRENT_VERSION = '2.0'

 /**
 * Convert UUID auth_user_id to integer user_id
 * Handles both UUID format and db_ prefixed IDs
 */
 private static async getUserIdFromAuthId(authUserId: string): Promise<number | null> {
 try {
 console.log('LEGAL SERVICE: getUserIdFromAuthId called with:', authUserId)

 // Handle db_ prefixed IDs (direct database user IDs, including malformed "db_db_db_XXX")
 if (authUserId.includes('db_')) {
 // Remove ALL occurrences of "db_" prefix (handles malformed IDs like "db_db_db_567")
 const dbUserId = parseInt(authUserId.replace(/db_/g, ''));
 console.log('LEGAL SERVICE: Using db_ prefixed ID for legal documents:', dbUserId);
 return dbUserId;
 }

 // Handle direct numeric IDs
 if (/^\d+$/.test(authUserId)) {
 const numericId = parseInt(authUserId);
 console.log('LEGAL SERVICE: Using direct numeric ID for legal documents:', numericId);
 return numericId;
 }

 // Handle UUID format - lookup in database via API
 console.log('LEGAL SERVICE: Looking up UUID in database:', authUserId);
 const result = await callLegalDocumentAPI('resolveUserId', { authUserId });

 console.log('LEGAL SERVICE: UUID lookup result:', result);

 if (!result.success || !result.userId) {
 console.error('LEGAL SERVICE: Error finding user by auth_user_id:', result.error)
 return null
 }

 console.log('LEGAL SERVICE: Found user ID from UUID:', result.userId);
 return result.userId
 } catch (error: any) {
 console.error('LEGAL SERVICE: Exception in getUserIdFromAuthId:', error)
 console.error('LEGAL SERVICE: Exception details:', {
 message: error?.message,
 stack: error?.stack,
 input_auth_user_id: authUserId
 })
 return null
 }
 }

 /**
 * Record a user's agreement to a legal document
 * @param userIdOrAuthId - Can be either integer user_id or UUID auth_user_id
 */
 static async recordAgreement(
 userIdOrAuthId: number | string,
 documentType: 'terms_conditions' | 'privacy_policy' | 'disclaimer' | 'share_purchase_agreement',
 ipAddress?: string,
 userAgent?: string
 ): Promise<{ success: boolean; error?: string }> {
 try {
 console.log('LEGAL SERVICE: recordAgreement called with:', {
 userIdOrAuthId,
 type: typeof userIdOrAuthId,
 documentType
 })

 // Convert UUID to integer user_id if needed
 let userId: number
 if (typeof userIdOrAuthId === 'string') {
 const convertedId = await this.getUserIdFromAuthId(userIdOrAuthId)
 console.log('LEGAL SERVICE: Converted string ID to:', convertedId)
 if (!convertedId) {
 console.error('LEGAL SERVICE: User not found for auth ID:', userIdOrAuthId)
 return { success: false, error: 'User not found' }
 }
 userId = convertedId
 } else {
 userId = userIdOrAuthId
 }

 // CRITICAL VALIDATION: Ensure userId is valid before inserting
 if (!userId || userId === null || userId === undefined || userId <= 0 || !Number.isInteger(userId)) {
 console.error('LEGAL SERVICE: INVALID USER ID - Cannot record agreement!', {
 userId,
 type: typeof userId,
 original: userIdOrAuthId
 })
 return { success: false, error: 'Invalid user ID - cannot record agreement' }
 }

 console.log('LEGAL SERVICE: Valid user ID confirmed:', userId, 'Recording agreement for:', documentType)

 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callLegalDocumentAPI('recordAgreement', {
 userId,
 documentType,
 documentVersion: this.CURRENT_VERSION,
 ipAddress,
 userAgent
 });

 if (!result.success) {
 console.error('LEGAL SERVICE: Database error recording legal agreement:', result.error)
 return { success: false, error: result.error }
 }

 console.log('LEGAL SERVICE: Agreement recorded successfully for user', userId, 'document:', documentType)
 return { success: true }
 } catch (error: any) {
 console.error('LEGAL SERVICE: Exception in recordAgreement:', error)
 return { success: false, error: 'Failed to record agreement' }
 }
 }

 /**
 * Check if user has agreed to all current legal documents
 * @param userIdOrAuthId - Can be either integer user_id or UUID auth_user_id
 */
 static async checkAgreementStatus(userIdOrAuthId: number | string): Promise<AgreementStatus> {
 try {
 console.log('LEGAL SERVICE: checkAgreementStatus called with:', userIdOrAuthId, 'Type:', typeof userIdOrAuthId)

 // BROWSER: Use API endpoint to avoid RLS issues
 if (typeof window !== 'undefined') {
 console.log('LEGAL SERVICE: Browser detected, using API endpoint')
 return await this.checkAgreementStatusViaAPI(userIdOrAuthId)
 }

 // SERVER-SIDE: Use direct database access with service role
 return await this.checkAgreementStatusDirect(userIdOrAuthId)
 } catch (error: any) {
 // v4.8.248: re-throw. Returning an all-false status here silently converted every
 // transient failure into "compliance required", which is what forced users to
 // re-accept all four documents every few days. Callers already handle the throw by
 // leaving the user's current access untouched and showing a soft warning.
 console.error('LEGAL SERVICE: Exception in checkAgreementStatus:', error)
 throw error
 }
 }

 /**
 * Check agreement status via API endpoint (for browser use)
 */
 private static async checkAgreementStatusViaAPI(userIdOrAuthId: number | string): Promise<AgreementStatus> {
 try {
 // Convert to numeric ID for API
 let userId: string | number = userIdOrAuthId
 if (typeof userIdOrAuthId === 'string' && userIdOrAuthId.includes('db_')) {
 userId = userIdOrAuthId // Keep db_ prefix, API will handle it
 }

 // SECURITY v2.9.15: Add session headers — server now derives userId from session
 const sessionHeaders = await ensureSessionHeaders();
 const response = await fetch('/api/legal-document-status', {
 method: 'POST',
 headers: { 'Content-Type': 'application/json', ...sessionHeaders },
 body: JSON.stringify({})
 })

 if (!response.ok) {
 // v4.8.248: DO NOT return an all-false status here. An all-false result is
 // indistinguishable from "this user has genuinely accepted nothing", which made
 // the dashboard lock the user into compliance mode and re-prompt them for all four
 // documents after any transient API/session failure. Throw instead so the caller's
 // error handler keeps the user's existing access.
 console.error('LEGAL SERVICE: API error:', response.status)
 throw new Error(`Legal document status check failed (HTTP ${response.status})`)
 }

 const result = await response.json()
 console.log('LEGAL SERVICE: API response:', result)

 if (result.success && result.status) {
 return result.status
 }

 // v4.8.248: unreadable/failed payload is NOT proof of non-acceptance — throw.
 console.error('LEGAL SERVICE: API returned no usable status:', result?.error)
 throw new Error(result?.error || 'Legal document status unavailable')
 } catch (error) {
 // v4.8.248: propagate so the caller can preserve existing access instead of
 // treating an infrastructure failure as "user has accepted nothing".
 console.error('LEGAL SERVICE: API call failed:', error)
 throw error
 }
 }

 /**
 * Check agreement status directly via database (for server-side use)
 */
 private static async checkAgreementStatusDirect(userIdOrAuthId: number | string): Promise<AgreementStatus> {
 // Convert UUID to integer user_id if needed
 let userId: number
 if (typeof userIdOrAuthId === 'string') {
 console.log('LEGAL SERVICE: Converting string ID to numeric:', userIdOrAuthId)
 const convertedId = await this.getUserIdFromAuthId(userIdOrAuthId)

 if (!convertedId) {
 console.log('LEGAL SERVICE: Failed to convert user ID')
 return {
 terms_conditions: false,
 privacy_policy: false,
 disclaimer: false,
 share_purchase_agreement: false,
 all_agreed: false
 }
 }
 userId = convertedId
 } else {
 userId = userIdOrAuthId
 }

 if (!userId || userId <= 0 || !Number.isInteger(userId)) {
 console.error('LEGAL SERVICE: Invalid user ID:', userId)
 return {
 terms_conditions: false,
 privacy_policy: false,
 disclaimer: false,
 share_purchase_agreement: false,
 all_agreed: false
 }
 }

 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callLegalDocumentAPI('getAgreementStatus', { userId });

 if (!result.success) {
 console.error('LEGAL SERVICE: Database error:', result.error)
 return {
 terms_conditions: false,
 privacy_policy: false,
 disclaimer: false,
 share_purchase_agreement: false,
 all_agreed: false
 }
 }

 const agreements = result.agreements || [];

 const status: AgreementStatus = {
 terms_conditions: false,
 privacy_policy: false,
 disclaimer: false,
 share_purchase_agreement: false,
 all_agreed: false
 }

 if (agreements && agreements.length > 0) {
 for (const agreement of agreements) {
 if (agreement.document_type === 'terms_conditions') status.terms_conditions = true
 if (agreement.document_type === 'privacy_policy') status.privacy_policy = true
 if (agreement.document_type === 'disclaimer') status.disclaimer = true
 if (agreement.document_type === 'share_purchase_agreement') status.share_purchase_agreement = true
 }
 }

 status.all_agreed = status.terms_conditions && status.privacy_policy && status.disclaimer && status.share_purchase_agreement

 console.log('LEGAL SERVICE: Status for user', userId, ':', status)
 return status
 }

 /**
 * Get user's agreement history for a specific document type
 * @param userIdOrAuthId - Can be either integer user_id or UUID auth_user_id
 */
 static async getAgreementHistory(
 userIdOrAuthId: number | string,
 documentType?: 'terms_conditions' | 'privacy_policy' | 'disclaimer' | 'share_purchase_agreement'
 ): Promise<LegalDocumentAgreement[]> {
 try {
 // Convert UUID to integer user_id if needed
 let userId: number
 if (typeof userIdOrAuthId === 'string') {
 const convertedId = await this.getUserIdFromAuthId(userIdOrAuthId)
 if (!convertedId) {
 return []
 }
 userId = convertedId
 } else {
 userId = userIdOrAuthId
 }

 // Use API endpoint - NO getServiceRoleClient() in browser
 const result = await callLegalDocumentAPI('getAgreements', { userId, documentType });

 if (!result.success) {
 console.error('Error getting agreement history:', result.error)
 return []
 }

 return result.agreements || []
 } catch (error: any) {
 console.error('Error in getAgreementHistory:', error)
 return []
 }
 }

 /**
 * Check if user needs to agree to updated legal documents
 * @param userIdOrAuthId - Can be either integer user_id or UUID auth_user_id
 */
 static async checkForUpdatedDocuments(userIdOrAuthId: number | string): Promise<{
 needsUpdate: boolean
 documentsToUpdate: string[]
 }> {
 try {
 const status = await this.checkAgreementStatus(userIdOrAuthId)
 const documentsToUpdate: string[] = []

 if (!status.terms_conditions) documentsToUpdate.push('Terms & Conditions')
 if (!status.privacy_policy) documentsToUpdate.push('Privacy Policy')
 if (!status.disclaimer) documentsToUpdate.push('Disclaimer')
 if (!status.share_purchase_agreement) documentsToUpdate.push('Share Purchase Agreement')

 return {
 needsUpdate: documentsToUpdate.length > 0,
 documentsToUpdate
 }
 } catch (error: any) {
 console.error('Error checking for updated documents:', error)
 return {
 needsUpdate: true,
 documentsToUpdate: ['Terms & Conditions', 'Privacy Policy', 'Disclaimer', 'Share Purchase Agreement']
 }
 }
 }

 /**
 * Get client IP address (for browser environment)
 */
 static async getClientIP(): Promise<string | undefined> {
 try {
 // In a real application, you might want to use a service to get the client IP
 // For now, we'll return undefined and handle it gracefully
 return undefined
 } catch (error: any) {
 console.error('Error getting client IP:', error)
 return undefined
 }
 }

 /**
 * Get user agent string
 */
 static getUserAgent(): string | undefined {
 try {
 return typeof navigator !== 'undefined' ? navigator.userAgent : undefined
 } catch (error: any) {
 console.error('Error getting user agent:', error)
 return undefined
 }
 }

 /**
 * Record agreement with automatic IP and user agent detection
 * @param userIdOrAuthId - Can be either integer user_id or UUID auth_user_id
 */
 static async recordAgreementWithContext(
 userIdOrAuthId: number | string,
 documentType: 'terms_conditions' | 'privacy_policy' | 'disclaimer' | 'share_purchase_agreement'
 ): Promise<{ success: boolean; error?: string }> {
 const ipAddress = await this.getClientIP()
 const userAgent = this.getUserAgent()

 return this.recordAgreement(userIdOrAuthId, documentType, ipAddress, userAgent)
 }

 /**
 * Bulk record agreements for all legal documents
 * @param userIdOrAuthId - Can be either integer user_id or UUID auth_user_id
 */
 static async recordAllAgreements(userIdOrAuthId: number | string): Promise<{
 success: boolean
 results: { [key: string]: boolean }
 errors: string[]
 }> {
 const documentTypes: Array<'terms_conditions' | 'privacy_policy' | 'disclaimer' | 'share_purchase_agreement'> = [
 'terms_conditions',
 'privacy_policy',
 'disclaimer',
 'share_purchase_agreement'
 ]

 const results: { [key: string]: boolean } = {}
 const errors: string[] = []

 for (const docType of documentTypes) {
 const result = await this.recordAgreementWithContext(userIdOrAuthId, docType)
 results[docType] = result.success

 if (!result.success && result.error) {
 errors.push(`${docType}: ${result.error}`)
 }
 }

 const allSuccess = Object.values(results).every(success => success)

 return {
 success: allSuccess,
 results,
 errors
 }
 }

 /**
 * Check if user can purchase shares (must agree to all legal documents first)
 * @param userIdOrAuthId - Can be either integer user_id or UUID auth_user_id
 */
 static async canUserPurchaseShares(userIdOrAuthId: number | string): Promise<{
 canPurchase: boolean
 missingAgreements: string[]
 message: string
 }> {
 try {
 const status = await this.checkAgreementStatus(userIdOrAuthId)

 if (status.all_agreed) {
 return {
 canPurchase: true,
 missingAgreements: [],
 message: 'All legal documents agreed to. Share purchase allowed.'
 }
 }

 const missingAgreements: string[] = []
 if (!status.terms_conditions) missingAgreements.push('Terms & Conditions')
 if (!status.privacy_policy) missingAgreements.push('Privacy Policy')
 if (!status.disclaimer) missingAgreements.push('Legal Disclaimer')
 if (!status.share_purchase_agreement) missingAgreements.push('Share Purchase Agreement')

 return {
 canPurchase: false,
 missingAgreements,
 message: `You must agree to all legal documents before purchasing shares. Missing: ${missingAgreements.join(', ')}`
 }
 } catch (error: any) {
 console.error('Error checking share purchase eligibility:', error)
 return {
 canPurchase: false,
 missingAgreements: ['Terms & Conditions', 'Privacy Policy', 'Legal Disclaimer'],
 message: 'Unable to verify legal document agreements. Please ensure all documents are agreed to before purchasing shares.'
 }
 }
 }

 /**
 * Get user-friendly names for document types
 */
 static getDocumentDisplayName(documentType: 'terms_conditions' | 'privacy_policy' | 'disclaimer'): string {
 const displayNames = {
 'terms_conditions': 'Terms & Conditions',
 'privacy_policy': 'Privacy Policy',
 'disclaimer': 'Legal Disclaimer'
 }
 return displayNames[documentType]
 }

 /**
 * Validate that all required legal documents exist and are accessible
 */
 static validateLegalDocumentsSystem(): {
 isValid: boolean
 issues: string[]
 } {
 const issues: string[] = []

 // Check if all required document types are defined
 const currentVersion: string = this.CURRENT_VERSION

 if (!currentVersion || currentVersion.length === 0) {
 issues.push('Document version not defined')
 }

 // Additional validation could be added here
 // e.g., checking if document components exist, database connectivity, etc.

 return {
 isValid: issues.length === 0,
 issues
 }
 }
}

export default LegalDocumentService
